import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

export const EMAIL_TEMPLATES_PATH = '/insure/internal/email-templates'

export interface EmailTemplateOwner {
  id: string
  name: string
}

export interface EmailTemplate {
  id: string
  /** Blank means the subject names the template. */
  name: string
  subject: string
  body: string
  /** The name, or the subject when there isn't one. */
  displayName: string
  /** Shared with every staff user; `owner` is then its author. */
  isGlobal: boolean
  /** Whether it's the signed-in rep's own default. */
  isDefault: boolean
  owner: EmailTemplateOwner
  createdAt: string
  updatedAt: string
}

type EmailTemplateEdits = Partial<Pick<EmailTemplate, 'name' | 'subject' | 'body'>>

const BASE = '/api/v1/insure/internal/email-templates'

function listTemplates(): Promise<EmailTemplate[]> {
  return axios.get(`${BASE}/`).then((r) => {
    const templates = toCamelCase(r.data) as EmailTemplate[]
    return [...templates].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1
      return (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' })
    })
  })
}

// Kept in the server's order (by owner) so the page can group them.
function listOthersTemplates(): Promise<EmailTemplate[]> {
  return axios
    .get(`${BASE}/`, { params: { scope: 'others' } })
    .then((r) => toCamelCase(r.data) as EmailTemplate[])
}

function createTemplate(data: { subject: string; body: string; isGlobal: boolean }): Promise<EmailTemplate> {
  return axios.post(`${BASE}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as EmailTemplate)
}

function updateTemplate(id: string, data: EmailTemplateEdits): Promise<EmailTemplate> {
  return axios.patch(`${BASE}/${id}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as EmailTemplate)
}

function deleteTemplate(id: string): Promise<void> {
  return axios.delete(`${BASE}/${id}/`).then(() => undefined)
}

function setDefaultTemplate(id: string): Promise<EmailTemplate> {
  return axios.post(`${BASE}/${id}/set-default/`).then((r) => toCamelCase(r.data) as EmailTemplate)
}

/** Global templates plus the rep's own: everything a composer offers. */
export function useEmailTemplates() {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_TEMPLATES],
    queryFn: listTemplates,
  })
}

/** Other staff users' personal templates, for the Email Templates page only. */
export function useOthersEmailTemplates() {
  return useQuery({
    queryKey: [CacheKeys.EMAIL_TEMPLATES, 'others'],
    queryFn: listOthersTemplates,
  })
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.EMAIL_TEMPLATES] }),
  })
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & EmailTemplateEdits) => updateTemplate(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.EMAIL_TEMPLATES] }),
  })
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.EMAIL_TEMPLATES] }),
  })
}

export function useSetDefaultEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => setDefaultTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.EMAIL_TEMPLATES] }),
  })
}
